import { useCallback, useMemo, useState } from 'react';
import { useToast } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import DecisionFooter from '../../DecisionFooter.jsx';
import { useEventDraft } from '../../../context/useEventDraft.js';
import { useWorkQueueContext } from '../../../context/useWorkQueueContext.js';
import { tallyClaimDecisions } from '../shared/claims/index.js';
import { isDecisionSkipped } from '../../../utils/decisionSkipped.js';
import { CheckCheck, ShieldCheck } from 'lucide-react';

const EMPTY_CLAIMS = [];

/**
 * Gov Promotion DECISION bar: Approve all non-conflicted + Submit attestation,
 * gated on every conflicted claim having a decision. Per-claim Approve/Reject/Correct
 * only update EventDraft scratch state — no GS calls until Confirm Attestation,
 * when corrections, removals, mark-review, and submit run as one batch.
 */
function GovPromotionDecision({ event, actions }) {
  const { t } = useTranslation('work-queues-app');
  const toast = useToast();
  const { completeGovPromotion, pendingDecisionIds } = useWorkQueueContext();
  const { itemDecisions, itemCorrections, setItemDecisions } = useEventDraft();
  const [confirming, setConfirming] = useState(false);
  const isResolving = pendingDecisionIds?.has(event.id) ?? false;

  const claims = event.detail?.claims ?? EMPTY_CLAIMS;

  const { nonConflictedPending, canSubmit, allDecided, counts } = useMemo(() => {
    const conflicted = claims.filter((claim) => claim.conflict);
    return {
      nonConflictedPending: claims.some((claim) => !claim.conflict && !itemDecisions[claim.id]),
      canSubmit: conflicted.every((claim) => itemDecisions[claim.id]),
      allDecided: claims.length > 0 && claims.every((claim) => itemDecisions[claim.id]),
      counts: tallyClaimDecisions(claims, itemDecisions, { approveKey: 'promoted' }),
    };
  }, [claims, itemDecisions]);

  const approveAllNonConflicted = useCallback(() => {
    setItemDecisions((current) => {
      const next = { ...current };
      claims.forEach((claim) => {
        if (!claim.conflict && !next[claim.id]) next[claim.id] = 'approve';
      });
      return next;
    });
  }, [claims, setItemDecisions]);

  const handleConfirm = useCallback(async () => {
    if (isResolving) return;
    try {
      await completeGovPromotion(event.id, {
        ...counts,
        corrections: itemCorrections,
        decisions: itemDecisions,
        // The provider's own event copy never carries the GS-fetched claims (they
        // only exist on this preview-enriched `event` from useEventDetail) — hand
        // them over explicitly so the batch has real candidateClaimUuid/claimUuid
        // to write against instead of silently no-op'ing on an empty claims list.
        claims,
      });
      toast.success({
        title: t('govPromotion.attestedTitle'),
        message: t('govPromotion.attestedMessage'),
      });
      setConfirming(false);
    } catch (err) {
      if (isDecisionSkipped(err)) return;
      toast.error({
        title: t('govPromotion.submitErrorTitle'),
        message: [err?.message ?? t('govPromotion.submitErrorMessage'), t('govPromotion.submitErrorRefreshed')].join(
          ' ',
        ),
      });
    }
  }, [claims, completeGovPromotion, counts, event.id, isResolving, itemCorrections, itemDecisions, t, toast]);

  const primaryActions = useMemo(() => {
    if (confirming) {
      return [
        {
          id: 'cancel',
          label: t('actions.cancel'),
          variant: 'secondary',
          size: 'md',
          disabled: isResolving,
          onClick: () => setConfirming(false),
        },
        {
          id: 'confirm',
          label: isResolving ? t('governance.resolving') : t('govPromotion.confirmAttestation'),
          size: 'md',
          disabled: isResolving,
          onClick: handleConfirm,
        },
      ];
    }
    return [
      {
        id: 'approve-all',
        label: t('govPromotion.approveAll'),
        variant: 'secondary',
        size: 'md',
        icon: CheckCheck,
        disabled: !nonConflictedPending,
        onClick: approveAllNonConflicted,
      },
      {
        id: 'submit',
        label: t('govPromotion.submitAttestation'),
        size: 'md',
        icon: ShieldCheck,
        disabled: !canSubmit,
        onClick: () => setConfirming(true),
      },
    ];
  }, [confirming, t, handleConfirm, isResolving, nonConflictedPending, approveAllNonConflicted, canSubmit]);

  // Product delta vs the prototype: the summary appears as soon as every claim
  // is decided, not only after Submit, so the reviewer sees what they are about
  // to attest before committing to the confirm step.
  // Pluralize on `promoted` — that is the grammatical subject of confirmSummary.
  const summary =
    confirming || allDecided
      ? t('govPromotion.confirmSummary', {
          ...counts,
          count: counts.promoted,
          destination: event.destination ?? '',
        })
      : undefined;
  const hint = (!canSubmit && t('govPromotion.submitHint')) || undefined;

  return (
    <DecisionFooter
      event={event}
      primaryActions={primaryActions}
      hint={hint}
      summary={summary}
      onSkip={actions.skip}
      onAsk={actions.ask}
      onEscalate={actions.escalate}
      onTrace={actions.trace}
      extraMenuItems={actions.extraMenuItems}
      sticky={actions.sticky}
    />
  );
}

export default GovPromotionDecision;
